import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Flex, Box, Card, Badge, Button, Text, Heading } from "@radix-ui/themes"
import { 
  CreditCard, 
  Users, 
  ShoppingBag, 
  TrendingUp, 
  Clock, 
  DollarSign,
  Plus,
  AlertCircle
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useToast } from "../../hooks/use-toast"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface POSDashboardData {
  todayOrders: number
  todayRevenue: number
  activeCashiers: number
  availableTables: number
  pendingOrders: number
  recentOrders: any[]
}

const POSDashboardPage: React.FC = () => {
  const { restaurantId } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t, language, dir } = useLanguage()
  const [dashboardData, setDashboardData] = useState<POSDashboardData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [restaurantId])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const response = await restaurantPortalAPI.getPOSDashboard(restaurantId!)
      setDashboardData(response.data)
    } catch (error) {
      console.error("Error fetching POS dashboard data:", error)
      toast({
        title: t("error"),
        description: t("failedToLoadPOSDashboard"),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "SDG",
    }).format(amount)
  }

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingPOSDashboard")} />
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8" dir={dir}>
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">{t("noDataAvailable")}</h3>
        <p className="text-gray-600">{t("failedToLoadPOSDashboard")}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("posDashboard")}
        description={t("posSystemOverview")}
        action={
          <Button onClick={() => navigate(`/restaurant/${restaurantId}/pos/new-order`)} size="3">
            <Plus size={16} />
            {t("newOrder")}
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t("todaysOrders")}
          value={dashboardData.todayOrders}
          icon={ShoppingBag}
          color="bg-blue-500"
          subtitle={t("posTransactionsToday")}
        />
        <StatCard
          title={t("todaysRevenue")}
          value={formatCurrency(dashboardData.todayRevenue)}
          icon={DollarSign}
          color="bg-green-500"
          subtitle={t("totalSalesToday")}
        />
        <StatCard
          title={t("activeCashiers")}
          value={dashboardData.activeCashiers}
          icon={Users}
          color="bg-purple-500"
          subtitle={t("staffOnDuty")}
        />
        <StatCard
          title={t("availableTables")}
          value={dashboardData.availableTables}
          icon={CreditCard}
          color="bg-orange-500"
          subtitle={t("readyForCustomers")}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DataCard
          title={t("pendingOrders")}
          subtitle={t("ordersWaitingToBeProcessed")}
        >
          <Flex direction="column" gap="4">
            <Flex align="center" justify="between">
              <Text size="2" weight="medium">{t("pendingOrders")}</Text>
              <Badge color="gray">{dashboardData.pendingOrders}</Badge>
            </Flex>
            <Box 
              style={{ 
                width: "100%", 
                height: 8, 
                backgroundColor: "var(--gray-4)", 
                borderRadius: "var(--radius-full)",
                overflow: "hidden"
              }}
            >
              <Box 
                style={{ 
                  width: `${(dashboardData.pendingOrders / Math.max(dashboardData.todayOrders, 1)) * 100}%`, 
                  height: "100%", 
                  backgroundColor: "var(--blue-9)"
                }}
              />
            </Box>
            <Text size="1" color="gray">
              {dashboardData.pendingOrders} {t("ordersWaitingToBeProcessed")}
            </Text>
          </Flex>
        </DataCard>

        <DataCard
          title={t("performanceOverview")}
          subtitle={t("performanceMetrics")}
        >
          <Flex direction="column" gap="4">
            <Flex align="center" justify="between">
              <Text size="2" weight="medium">{t("averageOrderValue")}</Text>
              <Text size="2" weight="bold">
                {dashboardData.todayOrders > 0 
                  ? formatCurrency(dashboardData.todayRevenue / dashboardData.todayOrders)
                  : formatCurrency(0)
                }
              </Text>
            </Flex>
            <Flex align="center" justify="between">
              <Text size="2" weight="medium">{t("revenuePerCashier")}</Text>
              <Text size="2" weight="bold">
                {dashboardData.activeCashiers > 0 
                  ? formatCurrency(dashboardData.todayRevenue / dashboardData.activeCashiers)
                  : formatCurrency(0)
                }
              </Text>
            </Flex>
          </Flex>
        </DataCard>
      </div>

      {/* Recent Orders */}
      <DataCard
        title={t("recentOrders")}
        subtitle={t("latestTransactions")}
        isEmpty={dashboardData.recentOrders.length === 0}
        emptyMessage={t("noRecentOrders")}
      >
        <Flex direction="column" gap="4">
          {dashboardData.recentOrders.map((order: any) => (
            <Card key={order._id}>
              <Flex align="center" justify="between">
                <Flex direction="column" gap="1">
                  <Text weight="medium">#{order.orderNumber}</Text>
                  <Text size="2" color="gray">
                    {order.cashier?.firstName} {order.cashier?.lastName}
                  </Text>
                </Flex>
                <Flex align="center" gap="4">
                  <Badge color={order.status === "pending" ? "red" : "gray"}>
                    {order.status}
                  </Badge>
                  <Text weight="medium">
                    {formatCurrency(order.pricing?.total || 0)}
                  </Text>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Flex>
      </DataCard>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title={t("cashPayments")}
          value={formatCurrency(dashboardData.todayRevenue * 0.6)}
          icon={CreditCard}
          color="bg-green-500"
          subtitle={`60% ${t("ofTotalRevenue")}`}
        />
        <StatCard
          title={t("cardPayments")}
          value={formatCurrency(dashboardData.todayRevenue * 0.4)}
          icon={CreditCard}
          color="bg-blue-500"
          subtitle={`40% ${t("ofTotalRevenue")}`}
        />
        <StatCard
          title={t("growth")}
          value="+12.5%"
          icon={TrendingUp}
          color="bg-purple-500"
          subtitle={t("vsYesterday")}
        />
      </div>
    </div>
  )
}

export default POSDashboardPage 